import { ClipType, Clipper, ClipperOffset, EndType, FillRule, JoinType, PolyTree64, type Path64, type Paths64 } from 'clipper2-ts';
import { CLIPPER_SCALE } from '../config';
import type { Poly } from './polygon';

// Clipper2 wrappers mirroring the reference's Clipper 6 helpers (P/Misc.cpp:406-740, SPEC §1).
// Integer scale: CLIPPER_SCALE units per cm (the reference truncates to whole cm).

export function toPath(p: Poly): Path64 {
  return p.map((q) => ({ x: Math.round(q.x * CLIPPER_SCALE), y: Math.round(q.y * CLIPPER_SCALE) }));
}

export function toPaths(ps: Poly[]): Paths64 {
  return ps.filter((p) => p.length >= 3).map(toPath);
}

export function fromPath(p: Path64): Poly {
  return p.map((q) => ({ x: q.x / CLIPPER_SCALE, y: q.y / CLIPPER_SCALE }));
}

export function fromPaths(ps: Paths64): Poly[] {
  return ps.map(fromPath);
}

function largest(ps: Paths64): Poly {
  let best: Path64 | null = null;
  let bestA = 0;
  for (const p of ps) {
    const a = Math.abs(Clipper.area(p));
    if (a > bestA) {
      bestA = a;
      best = p;
    }
  }
  return best ? fromPath(best) : [];
}

export const JOIN = { miter: JoinType.Miter, square: JoinType.Square, round: JoinType.Round } as const;
export type Join = keyof typeof JOIN;

/** Raw offset of closed polygons; positive delta (cm) grows. Clipper 6 defaults: miter 2, arc 0.25. */
export function offsetPaths(ps: Poly[], delta: number, join: Join = 'miter'): Poly[] {
  if (ps.length === 0) return [];
  const co = new ClipperOffset(2.0, 0.25 * CLIPPER_SCALE);
  co.addPaths(toPaths(ps), JOIN[join], EndType.Polygon);
  const out: Paths64 = [];
  co.execute(delta * CLIPPER_SCALE, out);
  return fromPaths(out);
}

/** Buffer open polylines (e.g. road centrelines) by r with round ends. */
export function bufferLines(lines: Poly[], r: number): Poly[] {
  if (lines.length === 0) return [];
  const co = new ClipperOffset(2.0, 0.25 * CLIPPER_SCALE);
  co.addPaths(lines.filter((l) => l.length >= 2).map(toPath), JoinType.Round, EndType.Round);
  const out: Paths64 = [];
  co.execute(r * CLIPPER_SCALE, out);
  return fromPaths(out);
}

/** shrinkFMetaPoly: miter offset by −d, largest piece only. Negative d grows. */
export function shrink(p: Poly, d: number): Poly {
  if (p.length < 3) return [];
  const co = new ClipperOffset(2.0, 0.25 * CLIPPER_SCALE);
  co.addPath(toPath(p), JoinType.Miter, EndType.Polygon);
  const out: Paths64 = [];
  co.execute(-d * CLIPPER_SCALE, out);
  return largest(out);
}

/** Every piece of a shrink (the reference drops all but the largest; see ambiguity notes). */
export function shrinkAll(p: Poly, d: number, join: Join = 'miter'): Poly[] {
  return offsetPaths([p], -d, join);
}

/** mergeFMetaPoly: union, largest piece. */
export function merge(a: Poly, b: Poly): Poly {
  return largest(Clipper.union(toPaths([a, b]), FillRule.NonZero));
}

/** cutFMetaPoly: a − b, largest piece; returns a unchanged if the result is empty. */
export function cut(a: Poly, b: Poly): Poly {
  const r = Clipper.difference(toPaths([a]), toPaths([b]), FillRule.NonZero);
  return r.length === 0 ? a : largest(r);
}

/** trimToOutlineFMetaPoly: a ∩ b, largest piece. */
export function trim(a: Poly, b: Poly): Poly {
  return largest(Clipper.intersect(toPaths([a]), toPaths([b]), FillRule.NonZero));
}

export function unionAll(ps: Poly[]): Poly[] {
  return fromPaths(Clipper.union(toPaths(ps), FillRule.NonZero));
}

export function differenceAll(subject: Poly[], clip: Poly[]): Poly[] {
  return fromPaths(Clipper.difference(toPaths(subject), toPaths(clip), FillRule.NonZero));
}

export function intersectAll(subject: Poly[], clip: Poly[]): Poly[] {
  return fromPaths(Clipper.intersect(toPaths(subject), toPaths(clip), FillRule.NonZero));
}

/** Morphological opening (shrink d then grow d): removes parts narrower than 2d. */
export function open(p: Poly, d: number): Poly {
  return shrink(shrink(p, d), -d);
}

/** Morphological closing (grow d then shrink d). */
export function close(p: Poly, d: number): Poly {
  return shrink(shrink(p, -d), d);
}

/** CleanPolygons-ish: drop vertices within `distance` cm of the line through their neighbours. */
export function simplify(ps: Poly[], distance: number): Poly[] {
  return fromPaths(Clipper.simplifyPaths(toPaths(ps), distance * CLIPPER_SCALE, true)).filter((p) => p.length >= 3);
}

export function pathArea(p: Poly): number {
  return Clipper.area(toPath(p)) / (CLIPPER_SCALE * CLIPPER_SCALE);
}

export interface PolyNode {
  outer: Poly;
  holes: Poly[];
}

/**
 * Inward round offset returning a tree of outer polygons with their holes (ClipperOffset → PolyTree,
 * as in checkIfFullyTraversable, P/Misc.h:739-758).
 */
export function offsetTree(ps: Poly[], delta: number, join: Join = 'round', arcTolerance = 0.25): PolyNode[] {
  if (ps.length === 0) return [];
  const co = new ClipperOffset(2.0, arcTolerance * CLIPPER_SCALE);
  co.addPaths(toPaths(ps), JOIN[join], EndType.Polygon);
  const tree = new PolyTree64();
  co.execute(delta * CLIPPER_SCALE, tree);
  const out: PolyNode[] = [];
  const walk = (node: PolyTree64 | ReturnType<PolyTree64['child']>) => {
    for (let i = 0; i < node.count; i++) {
      const c = node.child(i);
      if (!c.isHole && c.poly) {
        const holes: Poly[] = [];
        for (let j = 0; j < c.count; j++) {
          const h = c.child(j);
          if (h.poly) holes.push(fromPath(h.poly));
          walk(h);
        }
        out.push({ outer: fromPath(c.poly), holes });
      }
    }
  };
  walk(tree);
  return out;
}

/** Boolean op result as outer+holes tree. */
export function booleanTree(op: 'union' | 'difference' | 'intersection', subject: Poly[], clip: Poly[]): PolyNode[] {
  const tree = new PolyTree64();
  const ct = op === 'union' ? ClipType.Union : op === 'difference' ? ClipType.Difference : ClipType.Intersection;
  Clipper.booleanOpWithPolyTree(ct, toPaths(subject), clip.length ? toPaths(clip) : null, tree, FillRule.NonZero);
  const out: PolyNode[] = [];
  const walk = (node: PolyTree64 | ReturnType<PolyTree64['child']>) => {
    for (let i = 0; i < node.count; i++) {
      const c = node.child(i);
      if (!c.isHole && c.poly) {
        const holes: Poly[] = [];
        for (let j = 0; j < c.count; j++) {
          const h = c.child(j);
          if (h.poly) holes.push(fromPath(h.poly));
          walk(h);
        }
        out.push({ outer: fromPath(c.poly), holes });
      }
    }
  };
  walk(tree);
  return out;
}

